// frontend/admin-portal/src/hooks/usePermission.js
import { useAuth } from '../contexts/AuthContext';
import { checkPermission } from '@fiberhome/shared-ui';

/**
 * Convenient permission helpers.
 *
 * Returns:
 *   has(perm)    — single permission check
 *   hasAny([...]) — any-of check
 *   hasAll([...]) — all-of check
 *   permissions  — raw array
 */
export function usePermission() {
    const { user, hasPermission, hasAny, hasAll, permissions } = useAuth();

    // Fallback if AuthContext doesn't provide helpers
    const perms = Array.isArray(permissions)
        ? permissions
        : Array.isArray(user?.role?.permissions)
            ? user.role.permissions
            : [];

    const has = (requested) => {
        if (!requested) return true;
        if (typeof hasPermission === 'function') return hasPermission(requested);
        return checkPermission(perms, requested);
    };

    const anyOf = (list) => {
        if (!Array.isArray(list) || list.length === 0) return true;
        if (typeof hasAny === 'function') return hasAny(list);
        return list.some((p) => checkPermission(perms, p));
    };

    const allOf = (list) => {
        if (!Array.isArray(list) || list.length === 0) return true;
        if (typeof hasAll === 'function') return hasAll(list);
        return list.every((p) => checkPermission(perms, p));
    };

    return { has, hasAny: anyOf, hasAll: allOf, permissions: perms };
}